import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

// ---- graph & policies (snake_case, passed straight through from dpe-ai) ----
export interface Step {
  step_id: string;
  name: string;
  description: string;
  order: number;
}

export interface Transition {
  from_step_id: string;
  to_step_id: string;
  from_name: string;
  to_name: string;
  condition: string | null;
}

export interface Flow {
  flow_id: string;
  name: string;
  review: boolean;
  steps: Step[];
  transitions: Transition[];
}

export type PolicyType =
  | 'eligibility'
  | 'approval'
  | 'limit'
  | 'obligation'
  | 'prohibition'
  | 'constraint';

export interface Policy {
  policy_id: string;
  statement: string;
  policy_type: PolicyType;
  evidence_text: string;
  review: boolean;
  source_chunk_ids: string[];
}

export interface Governs {
  policy_id: string;
  step_id: string;
}

export interface PolicyFlowGraph {
  tenant_id: string;
  flows: Flow[];
  policies: Policy[];
  governs: Governs[];
}

export interface PolicyListResponse {
  tenant_id: string;
  policies: Policy[];
}

// ---- rebuild status (camelCase; lastResult's fields are snake_case) ----
export type RebuildState = 'Idle' | 'Running' | 'Succeeded' | 'Failed';

export interface RebuildResult {
  tenant_id: string;
  chunk_count: number;
  policy_count: number;
  flow_count: number;
  step_count: number;
  transition_count: number;
  governs_edge_count: number;
}

export interface RebuildStatus {
  state: RebuildState;
  jobId: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  lastResult: RebuildResult | null;
  error: string | null;
}

// ---- review ----
export interface ReviewResult {
  status: string;
  node_type: 'policy' | 'flow';
  id: string;
  review: boolean;
}

// Every dpe-backend response is wrapped in this envelope; the real payload is
// always under `data`. There is no global unwrapping interceptor in this app,
// so we unwrap per-call with `map(res => res.data)` (same pattern as dlq.ts).
// Error responses (4xx/5xx) carry no `data` and surface as HttpErrorResponse;
// callers read the user-facing text from `error.message`.
interface ApiEnvelope<T> {
  statusCode: number;
  success: boolean;
  message: string;
  data: T;
}

/**
 * Talks to dpe-backend only (never dpe-ai) and never sends a tenant id — the
 * backend resolves the tenant from the JWT, which the authInterceptor attaches.
 */
@Injectable({ providedIn: 'root' })
export class PolicyFlow {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/policy-flow`;

  /** Starts a rebuild. 202 -> { jobId }. 409 (already running) / 400 (no tenant)
   *  surface as HttpErrorResponse to the caller's error handler. */
  rebuild(): Observable<{ jobId: string }> {
    return this.http
      .post<ApiEnvelope<{ jobId: string }>>(`${this.base}/rebuild`, {})
      .pipe(map(res => res.data));
  }

  status(): Observable<RebuildStatus> {
    return this.http
      .get<ApiEnvelope<RebuildStatus>>(`${this.base}/status`)
      .pipe(map(res => res.data));
  }

  /** 502 means dpe-ai is unreachable (surfaced as HttpErrorResponse). */
  graph(): Observable<PolicyFlowGraph> {
    return this.http
      .get<ApiEnvelope<PolicyFlowGraph>>(`${this.base}/graph`)
      .pipe(map(res => res.data));
  }

  policies(review?: boolean): Observable<PolicyListResponse> {
    let params = new HttpParams();
    if (review !== undefined) params = params.set('review', review);
    return this.http
      .get<ApiEnvelope<PolicyListResponse>>(`${this.base}/policies`, { params })
      .pipe(map(res => res.data));
  }

  setReview(nodeType: 'policy' | 'flow', id: string, review: boolean): Observable<ReviewResult> {
    return this.http
      .post<ApiEnvelope<ReviewResult>>(`${this.base}/review`, { nodeType, id, review })
      .pipe(map(res => res.data));
  }
}
